interface StoredData {
  id: string;
  data: any;
  timestamp: number;
  version: string;
  expiresAt: number;
  tags?: string[];
  compressed?: boolean;
}

interface CacheStrategy {
  name: string;
  ttl: number;
  maxSize: number;
  compression: boolean;
  priority: number;
}

class EnhancedIndexedDBManager {
  private db: IDBDatabase | null = null;
  private readonly dbName = 'NigeriaFxAdvancedDB';
  private readonly version = 3;
  private readonly strategies: Record<string, CacheStrategy> = {
    rates: { name: 'rates', ttl: 300000, maxSize: 1000, compression: false, priority: 1 },
    historical: { name: 'historical', ttl: 86400000, maxSize: 500, compression: true, priority: 2 },
    images: { name: 'images', ttl: 604800000, maxSize: 200, compression: false, priority: 3 },
    userPrefs: { name: 'userPrefs', ttl: 31536000000, maxSize: 100, compression: false, priority: 1 },
    analytics: { name: 'analytics', ttl: 2592000000, maxSize: 1000, compression: true, priority: 4 }
  };

  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        this.db = request.result;
        this.setupErrorHandling();
        this.startMaintenanceTasks();
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        this.createAdvancedStores(db);
      };
    });
  }

  private createAdvancedStores(db: IDBDatabase): void {
    const stores = [
      { name: 'rates', keyPath: 'id', indexes: [
        { name: 'timestamp', keyPath: 'timestamp' },
        { name: 'currency', keyPath: 'currency' },
        { name: 'tags', keyPath: 'tags', multiEntry: true }
      ]},
      { name: 'historical', keyPath: 'id', indexes: [
        { name: 'date', keyPath: 'date' },
        { name: 'currency', keyPath: 'currency' }
      ]},
      { name: 'images', keyPath: 'id', indexes: [
        { name: 'url', keyPath: 'url', unique: true },
        { name: 'timestamp', keyPath: 'timestamp' }
      ]},
      { name: 'userPreferences', keyPath: 'id' },
      { name: 'analytics', keyPath: 'id', indexes: [
        { name: 'event', keyPath: 'event' },
        { name: 'timestamp', keyPath: 'timestamp' }
      ]},
      { name: 'offlineQueue', keyPath: 'id', indexes: [
        { name: 'priority', keyPath: 'priority' },
        { name: 'timestamp', keyPath: 'timestamp' }
      ]}
    ];

    stores.forEach(storeConfig => {
      if (!db.objectStoreNames.contains(storeConfig.name)) {
        const store = db.createObjectStore(storeConfig.name, {
          keyPath: storeConfig.keyPath
        });

        storeConfig.indexes?.forEach(index => {
          store.createIndex(index.name, index.keyPath, { 
            unique: index.unique,
            multiEntry: index.multiEntry 
          });
        });
      }
    });
  }

  private setupErrorHandling(): void {
    if (this.db) {
      this.db.onerror = (event) => {
        console.error('IndexedDB error:', event);
      };

      this.db.onversionchange = () => {
        this.db?.close();
        console.warn('Database version changed, reloading...');
        window.location.reload();
      };
    }
  }

  private startMaintenanceTasks(): void {
    // Clean up expired data every hour
    setInterval(() => {
      this.cleanupExpiredData();
    }, 3600000);

    // Optimize storage every 6 hours
    setInterval(() => {
      this.optimizeStorage();
    }, 21600000);
  }

  async storeWithStrategy(
    storeName: string, 
    key: string, 
    data: any, 
    tags?: string[]
  ): Promise<void> {
    if (!this.db) await this.initialize();

    const strategy = this.strategies[storeName];
    if (!strategy) throw new Error(`Unknown storage strategy: ${storeName}`);

    const transaction = this.db!.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);

    // Compress data if strategy requires it
    const processedData = strategy.compression ? this.compressData(data) : data;

    const storedData: StoredData = {
      id: key,
      data: processedData,
      timestamp: Date.now(),
      version: '2.0.0',
      expiresAt: Date.now() + strategy.ttl,
      tags,
      compressed: strategy.compression
    };

    return new Promise((resolve, reject) => {
      const request = store.put(storedData);
      request.onsuccess = () => {
        this.enforceStorageLimit(storeName, strategy);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getWithStrategy(storeName: string, key: string): Promise<any | null> {
    if (!this.db) await this.initialize();

    const transaction = this.db!.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      
      request.onsuccess = () => {
        const result = request.result as StoredData;
        
        if (!result || Date.now() > result.expiresAt) {
          resolve(null);
          return;
        }

        // Decompress if needed
        const data = result.compressed ? this.decompressData(result.data) : result.data;
        resolve(data);
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  private compressData(data: any): string {
    try {
      const jsonString = JSON.stringify(data);
      return btoa(jsonString);
    } catch (error) {
      console.error('Compression failed:', error);
      return data;
    }
  }

  private decompressData(compressedData: string): any {
    try {
      const jsonString = atob(compressedData);
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Decompression failed:', error);
      return compressedData;
    }
  }

  private async enforceStorageLimit(storeName: string, strategy: CacheStrategy): Promise<void> {
    const transaction = this.db!.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const index = store.index('timestamp');

    const countRequest = store.count();
    countRequest.onsuccess = () => {
      if (countRequest.result > strategy.maxSize) {
        const deleteCount = countRequest.result - strategy.maxSize;
        const cursorRequest = index.openCursor();
        let deleted = 0;

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (cursor && deleted < deleteCount) {
            cursor.delete();
            deleted++;
            cursor.continue();
          }
        };
      }
    };
  }

  async cleanupExpiredData(): Promise<void> {
    if (!this.db) return;

    const storeNames = Object.keys(this.strategies);
    const now = Date.now();

    for (const storeName of storeNames) {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.openCursor();

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const data = cursor.value as StoredData;
          if (now > data.expiresAt) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    }
  }

  async optimizeStorage(): Promise<void> {
    const usage = await this.getStorageUsage();
    console.log('Storage optimization - Usage:', usage);

    if (usage.used / usage.quota > 0.8) {
      console.warn('Storage usage high, cleaning up...');
      await this.cleanupExpiredData();
    }
  }

  async getStorageUsage(): Promise<{ used: number; quota: number; percentage: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const used = estimate.usage || 0;
      const quota = estimate.quota || 0;
      return {
        used,
        quota,
        percentage: quota > 0 ? (used / quota) * 100 : 0
      };
    }
    return { used: 0, quota: 0, percentage: 0 };
  }
}

export const enhancedDB = new EnhancedIndexedDBManager();